import { Controller } from '@hotwired/stimulus'

const POLL_INTERVAL = 10000;
const MAX_POLL_ATTEMPTS = 12;

export default class extends Controller {
  static targets = ['amount'];
  static values = {
    url: String,
    balance: Number,
    loadingText: String,
    errorText: String,
  };

  connect() {
    if (this.hasBalanceValue) {
      this.#displayAmount(this.balanceValue);
    } else {
      this.#startPolling();
    }
  }

  disconnect() {
    this.#stopPolling();
  }

  refresh() {
    this.amountTarget.textContent = this.loadingTextValue;
    this.#startPolling(true);
  }

  #startPolling(forceRefresh = false) {
    this.#stopPolling();
    this.pollAttempts = 0;
    this.#poll(forceRefresh);
    this.pollTimer = window.setInterval(() => this.#poll(), POLL_INTERVAL);
  }

  #stopPolling() {
    if (!this.pollTimer) return;

    window.clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  async #poll(forceRefresh = false) {
    this.pollAttempts += 1;
    const balance = await this.#fetchBalance(forceRefresh);

    if (typeof balance === 'number') {
      this.#displayAmount(balance);
      this.#stopPolling();
      return;
    }

    if (this.pollAttempts >= MAX_POLL_ATTEMPTS) {
      this.amountTarget.textContent = this.errorTextValue;
      this.#stopPolling();
    }
  }

  async #fetchBalance(forceRefresh) {
    const url = forceRefresh ? `${this.urlValue}?force_refresh=true` : this.urlValue;

    try {
      const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (response.status !== 200) return null;

      const data = await response.json();
      return data?.investors_balance_sum;
    } catch (error) {
      console.error('Error fetching investors balance sum', error);
      return null;
    }
  }

  #displayAmount(value) {
    this.amountTarget.textContent = this.#formatAsCurrency(value);
  }

  #formatAsCurrency(value) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }
}
